import * as React from 'react';
import PropTypes from 'prop-types';
import { Field, Form, FormSpy } from 'react-final-form';
import RFTextField from '../form/RFTextField';
import FormFeedback from '../form/FormFeedback';
import { required } from '../form/validation';
import FormButton from '../form/FormButton';
import Button from '../components/Button';
import { Lightbulb } from '@mui/icons-material';
import { Box, Card, CardActions, Grid } from '@mui/material';
import { addUserWatchSubscription } from '../components/api';
import HintsDialog from './HintsDialog';
import logger from '../components/logger';

function NewWatchForm({ handleClickCancel, setRefresh, inputRef, userInfo, userWatchSubscriptions }) {

  const [isValid, setIsValid] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const validate = (values) => {
    const errors = required(['brand'], values);

    // prevent duplicates
    if (userWatchSubscriptions?.some(existingSubscription =>
        existingSubscription?.brand === values?.brand
        && existingSubscription?.description === values?.description)) {
      errors["description"] = "This subscription already exists above";
    }

    setIsValid(Object.keys(errors).length === 0);

    return errors;
  };

  const handleSubmit = ({ brand, description }, form) => {
    let ttl = 2147483647; // max
    if (!userInfo.collector) {
      const expiration = new Date();
      expiration.setDate(expiration.getDate() + 15);
      ttl = expiration.getTime().toString().slice(0, -3);
    }
    return addUserWatchSubscription(userInfo.id, brand, description, ttl)
      .then(() => {
        setRefresh(prevRefresh => ++prevRefresh);
        form.restart();
      }).catch(logger.error);
  };

  const handleClickHints = () => setOpen(true);
  const handleClickCloseHints = () => setOpen(false);

  return (
    <Form
      initialValues={{ description: "" }}
      onSubmit={handleSubmit}
      subscription={{ submitting: true, dirty: true }}
      validate={validate}
    >
      {({ handleSubmit: handleSubmit2, submitting, form }) => (
        <Box 
          component='form' 
          noValidate 
          onSubmit={(props) => handleSubmit2(props, form)}
        >
          <Card
            style={{ borderRadius: 0 }}
            variant="outlined"
            sx={{ m: 3 }}
          >
            <div>
              <Field
                sx={{ m: 3 }}
                autoComplete="brand"
                inputRef={inputRef}
                autoFocus
                component={RFTextField}
                disabled={submitting}
                label="Brand"
                margin="normal"
                name="brand"
                required
              />
            </div>
            <div>
              <Field
                sx={{ m: 3, mt: 0 }}
                autoComplete="description"
                component={RFTextField}
                disabled={submitting}
                label="Description"
                margin="normal"
                name="description"
                type="textarea"
              />
            </div>
            <FormSpy subscription={{ submitError: true }}>
              {({ submitError }) =>
                submitError ? (
                  <FormFeedback error sx={{ mt: 2 }}>
                    {submitError}
                  </FormFeedback>
                ) : null
              }
            </FormSpy>
            <CardActions disableSpacing>
              <Button size="medium" startIcon={<Lightbulb />} onClick={handleClickHints}>
                Hints & Examples
              </Button>
              <HintsDialog open={open} handleClose={handleClickCloseHints} />
            </CardActions>
          </Card>
          <Grid 
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button variant="contained" sx={{ mr: 3 }} onClick={handleClickCancel}>
              Cancel
            </Button>
            <FormButton
              disabled={!isValid || submitting}
              color="secondary"
              sx={{ mr: 3 }}
            >
              {submitting ? 'In progress…' : 'Submit'}
            </FormButton>
          </Grid>
        </Box>
      )}
    </Form>
  );
}

NewWatchForm.propTypes = {
  handleClickCancel: PropTypes.func,
};

export default NewWatchForm;